import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Building2 } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import ProjectDetailPanel from '../components/ProjectTracking/ProjectDetailPanel';
import { ContactNotesTasksModal } from '../components/ContactNotesTasksModal';
import api from '../services/api';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, value, onChange, type = 'text', textarea = false }) => (
  <div>
    <label className="block text-xs font-medium text-gray-400 mb-1">{label}</label>
    {textarea ? (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
      />
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
      />
    )}
  </div>
);

export default function SpellersProjectDetail({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [account, setAccount] = useState(null);
  const [zohoProject, setZohoProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalAccount, setModalAccount] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/spellers/projects/${id}`);
      setProject(res.data.project);
      setAccount(res.data.account);
      if (res.data.project?.zoho_project_id) {
        const zres = await api.get(`${API}/zoho/project/${res.data.project.zoho_project_id}`);
        setZohoProject(zres.data.project);
      }
    } catch (e) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setProject(p => ({ ...p, [key]: value }));
  const setAccountField = (key, value) => setAccount(a => ({ ...a, [key]: value }));

  const handleSaveProject = async () => {
    setSaving(true);
    try {
      const payload = {
        project_name: project.project_name,
        location: project.location,
        postcode: project.postcode,
        project_value: project.project_value,
        start_date: project.start_date,
        end_date: project.end_date,
        work_description: project.work_description,
        stage: project.stage,
        project_status: project.project_status,
        project_notes: project.project_notes,
        contacts: project.contacts,
      };
      const res = await api.put(`${API}/spellers/projects/${id}`, payload);
      setProject(res.data.project);
      toast.success('Project saved');
    } catch (e) {
      toast.error('Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAccount = async () => {
    if (!account) return;
    setSaving(true);
    try {
      const { id: accId, ...rest } = account;
      const res = await api.put(`${API}/spellers/accounts/${accId}`, rest);
      setAccount(res.data.account);
      toast.success('Account saved');
    } catch (e) {
      toast.error('Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const addContact = () => setProject(p => ({ ...p, contacts: [...(p.contacts || []), { name: '', phone: '', email: '', zoho_contact_id: null }] }));
  const updateContact = (idx, key, value) => setProject(p => {
    const contacts = [...(p.contacts || [])];
    contacts[idx] = { ...contacts[idx], [key]: value };
    return { ...p, contacts };
  });
  const removeContact = (idx) => setProject(p => ({ ...p, contacts: (p.contacts || []).filter((_, i) => i !== idx) }));

  if (loading || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        user={user}
        onLogout={onLogout}
        scrollWithPage
        subHeaderLeft={
          <button
            onClick={() => navigate('/spellers')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors text-gray-700"
            data-testid="spellers-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Spellers
          </button>
        }
      />

      <div className="max-w-5xl mx-auto px-6 py-6" data-testid="spellers-project-detail">

        {/* Spellers-only info card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-gray-900 text-lg">{project.project_name || project.project_id}</h2>
            <button
              onClick={handleSaveProject}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: '#ee7203' }}
              data-testid="spellers-save-project-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <Field label="Project Name" value={project.project_name} onChange={(v) => setField('project_name', v)} />
            <Field label="Location" value={project.location} onChange={(v) => setField('location', v)} />
            <Field label="Postcode" value={project.postcode} onChange={(v) => setField('postcode', v)} />
            <Field label="Project Value" value={project.project_value} onChange={(v) => setField('project_value', v)} />
            <Field label="Start Date" type="date" value={project.start_date} onChange={(v) => setField('start_date', v)} />
            <Field label="End Date" type="date" value={project.end_date} onChange={(v) => setField('end_date', v)} />
            <Field label="Stage" value={project.stage} onChange={(v) => setField('stage', v)} />
            <Field label="Project Status" value={project.project_status} onChange={(v) => setField('project_status', v)} />
          </div>
          <Field label="Work Description" textarea value={project.work_description} onChange={(v) => setField('work_description', v)} />
          <div className="mt-4">
            <Field label="Project Notes / Next Action" textarea value={project.project_notes} onChange={(v) => setField('project_notes', v)} />
          </div>

          {/* Package Progress — real live CRM data, reused component below shows/edits it */}
          <div className="mt-6 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Package Progress (live from CRM)</h3>
            <span className="text-xs font-semibold text-orange-600" data-testid="spellers-detail-package-progress">{project.package_progress}</span>
          </div>

          {/* Speller contacts */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Speller Contact(s)</h3>
              <button onClick={addContact} className="text-xs font-semibold text-orange-600 hover:underline" data-testid="spellers-add-contact-btn">+ Add contact</button>
            </div>
            <div className="space-y-2">
              {(project.contacts || []).map((c, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2">
                  <input value={c.name || ''} onChange={(e) => updateContact(i, 'name', e.target.value)} placeholder="Name" className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm" />
                  <input value={c.phone || ''} onChange={(e) => updateContact(i, 'phone', e.target.value)} placeholder="Phone" className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm" />
                  <input value={c.email || ''} onChange={(e) => updateContact(i, 'email', e.target.value)} placeholder="Email" className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm" />
                  <button onClick={() => removeContact(i)} className="text-xs text-gray-400 hover:text-red-500 px-2">Remove</button>
                </div>
              ))}
              {(project.contacts || []).length === 0 && <p className="text-xs text-gray-400">No contacts yet</p>}
            </div>
          </div>
        </div>

        {/* Tracked account (from Accounts sheet) */}
        {account && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6" data-testid="spellers-account-card">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => account.zoho_account_id && setModalAccount({ id: account.zoho_account_id, name: account.company })}
                className="flex items-center gap-2 font-bold text-gray-900 text-lg hover:text-orange-600 transition-colors"
                data-testid="spellers-open-account-modal-btn"
              >
                <Building2 className="w-5 h-5 text-gray-400" /> {account.company}
              </button>
              <button
                onClick={handleSaveAccount}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: '#ee7203' }}
                data-testid="spellers-save-account-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Field label="Trade" value={account.trade} onChange={(v) => setAccountField('trade', v)} />
              <Field label="Account Position" value={account.account_position} onChange={(v) => setAccountField('account_position', v)} />
              <Field label="Campaign Status" value={account.campaign_status} onChange={(v) => setAccountField('campaign_status', v)} />
              <Field label="Contact Name" value={account.contact_name} onChange={(v) => setAccountField('contact_name', v)} />
              <Field label="Telephone" value={account.telephone} onChange={(v) => setAccountField('telephone', v)} />
              <Field label="Email" value={account.email} onChange={(v) => setAccountField('email', v)} />
              <Field label="Next Action" value={account.next_action} onChange={(v) => setAccountField('next_action', v)} />
              <Field label="Due Date" type="date" value={account.due_date} onChange={(v) => setAccountField('due_date', v)} />
              <Field label="Priority" value={account.priority} onChange={(v) => setAccountField('priority', v)} />
            </div>
            <Field label="Notes" textarea value={account.notes} onChange={(v) => setAccountField('notes', v)} />
          </div>
        )}

        {/* Real CRM project — packages, quotes, notes/tasks (reused component) */}
        {zohoProject ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <ProjectDetailPanel project={zohoProject} user={user} onAddProjects={() => {}} />
          </div>
        ) : (
          <div className="text-sm text-gray-400 text-center py-8">This project isn't linked to a Zoho record yet.</div>
        )}
      </div>

      {modalAccount && (
        <ContactNotesTasksModal
          isOpen
          onClose={() => setModalAccount(null)}
          initialAccount={modalAccount}
        />
      )}
    </div>
  );
}
